/**
 * FX: conversions and rate selection. SQL public.get_fx_rate() is the source of
 * truth for stored documents; pickRate mirrors its rules for client previews
 * and live rates. Every document stores the rate it used (frozen at entry).
 */
import { D, MoneyDecimal, RATE_SCALE, convert, roundTo, type Dec, type DecimalInput } from './money';

export type FxSource = 'MANUAL' | 'TCMB_BUY' | 'TCMB_SELL' | 'FRANKFURTER' | 'ECB' | 'IDENTITY';

export interface FxRate {
  date: string; // yyyy-mm-dd
  from: string;
  to: string;
  rate: DecimalInput;
  source: FxSource;
}

export interface PickedRate {
  rate: Dec;
  source: string;
  date: string;
}

const PRIORITY: Record<string, number> = { MANUAL: 1, TCMB_BUY: 2, TCMB_SELL: 3, FRANKFURTER: 4, ECB: 5 };

function direct(rates: FxRate[], from: string, to: string, date: string): PickedRate | null {
  const candidates: { r: PickedRate; prio: number }[] = [];
  for (const r of rates) {
    if (r.date > date) continue;
    if (r.from === from && r.to === to) {
      candidates.push({ r: { rate: D(r.rate), source: r.source, date: r.date }, prio: PRIORITY[r.source] ?? 9 });
    } else if (r.from === to && r.to === from) {
      candidates.push({
        r: { rate: roundTo(new MoneyDecimal(1).dividedBy(D(r.rate)), RATE_SCALE), source: r.source, date: r.date },
        prio: (PRIORITY[r.source] ?? 9) + 10,
      });
    }
  }
  candidates.sort((a, b) => (a.r.date === b.r.date ? a.prio - b.prio : a.r.date < b.r.date ? 1 : -1));
  return candidates[0]?.r ?? null;
}

/** Latest rate on or before `date`; same-date ties by source priority; cross via TRY. */
export function pickRate(rates: FxRate[], from: string, to: string, date: string): PickedRate | null {
  if (from === to) return { rate: D(1), source: 'IDENTITY', date };
  const d = direct(rates, from, to, date);
  if (d) return d;
  if (from !== 'TRY' && to !== 'TRY') {
    const a = direct(rates, from, 'TRY', date);
    const b = direct(rates, to, 'TRY', date);
    if (a && b) {
      return {
        rate: roundTo(a.rate.dividedBy(b.rate), RATE_SCALE),
        source: `${a.source}/${b.source}`,
        date: a.date < b.date ? a.date : b.date,
      };
    }
  }
  return null;
}

export function toBase(amount: DecimalInput, rate: DecimalInput): Dec {
  return convert(amount, rate);
}

/** Convert an amount already in base currency into the reporting currency. */
export function baseToReporting(amountBase: DecimalInput, reportingRate: DecimalInput): Dec {
  const r = D(reportingRate);
  if (r.isZero()) throw new RangeError('Kur sıfır olamaz');
  return roundTo(D(amountBase).dividedBy(r), 4);
}

/**
 * FX difference at settlement, company perspective (+ gain, − loss).
 * sale: received more base currency than booked → gain. purchase: paid more → loss.
 */
export function settlementFxDiff(
  invoiceType: 'sale' | 'purchase',
  amount: DecimalInput,
  invoiceRate: DecimalInput,
  paymentBase: DecimalInput,
): Dec {
  const booked = convert(amount, invoiceRate);
  const paid = D(paymentBase);
  return invoiceType === 'sale' ? paid.minus(booked) : booked.minus(paid);
}

// ── providers ────────────────────────────────────────────────────────────────

/** Parse https://www.tcmb.gov.tr/kurlar/today.xml → rates to TRY (per 1 unit). */
export function parseTcmbXml(xml: string, date?: string): FxRate[] {
  const out: FxRate[] = [];
  const dateAttr = /Tarih="(\d{2})\.(\d{2})\.(\d{4})"/.exec(xml);
  const d = date ?? (dateAttr ? `${dateAttr[3]}-${dateAttr[2]}-${dateAttr[1]}` : '');
  const re = /<Currency[^>]*CurrencyCode="([A-Z]{3})"[^>]*>([\s\S]*?)<\/Currency>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml))) {
    const code = m[1]!;
    const body = m[2]!;
    const unit = Number(/<Unit>(\d+)<\/Unit>/.exec(body)?.[1] ?? '1');
    const buy = /<ForexBuying>([\d.]+)<\/ForexBuying>/.exec(body)?.[1];
    const sell = /<ForexSelling>([\d.]+)<\/ForexSelling>/.exec(body)?.[1];
    if (buy) out.push({ date: d, from: code, to: 'TRY', rate: roundTo(D(buy).dividedBy(unit), RATE_SCALE), source: 'TCMB_BUY' });
    if (sell) out.push({ date: d, from: code, to: 'TRY', rate: roundTo(D(sell).dividedBy(unit), RATE_SCALE), source: 'TCMB_SELL' });
  }
  return out;
}

export interface FrankfurterResponse {
  base?: string;
  date: string;
  rates: Record<string, number>;
}

/** Parse a frankfurter (ECB-based) response `{ base: 'USD', date, rates: { TRY: 49.2 } }`. */
export function parseFrankfurter(json: FrankfurterResponse, base: string): FxRate[] {
  return Object.entries(json.rates).map(([to, rate]) => ({
    date: json.date,
    from: json.base ?? base,
    to,
    rate: D(rate),
    source: 'FRANKFURTER' as const,
  }));
}

export const FRANKFURTER_URL = 'https://api.frankfurter.dev/v1/latest';

/** Live rate at entry time (anahtar gerektirmez). Throws on network/HTTP errors. */
export async function fetchLiveRate(from: string, to: string, fetchImpl: typeof fetch = fetch): Promise<PickedRate> {
  if (from === to) return { rate: D(1), source: 'IDENTITY', date: new Date().toISOString().slice(0, 10) };
  // never hang a form on a slow network: give up after 6 s and let the caller fall back to stored rates
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), 6000) : null;
  let res: Response;
  try {
    res = await fetchImpl(`${FRANKFURTER_URL}?base=${encodeURIComponent(from)}&symbols=${encodeURIComponent(to)}`, ctrl ? { signal: ctrl.signal } : undefined);
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (!res.ok) throw new Error(`Kur servisi yanıt vermedi (${res.status})`);
  const json = (await res.json()) as FrankfurterResponse;
  const r = json.rates[to];
  if (r === undefined) throw new Error(`Kur bulunamadı: ${from} → ${to}`);
  return { rate: D(r), source: 'FRANKFURTER', date: json.date };
}
